import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, ContextualField, DigitalCorrespondanceTable, DigitalSet, INamedGuid, Miniature } from '../../../../models';
import * as Fields from '../../../../common/helpers/contextual';
import { correspondanceTable, guidEmpty } from '../../../../common';
import { ILoggerService, IStoreService } from '../../../../services';

class DigitalSetContextualController implements angular.IComponentController {
    editedItem: DigitalCorrespondanceTable | DigitalSet;
    formController: angular.IFormController;
    miniatures: Miniature[];
    properties: ContextualField[];

    readonly allSets: DigitalSet[];
    readonly allCorrespondanceTables: DigitalSet[];
    readonly isDirtyFromOutside: boolean;
    readonly isInvalidFromOutside: boolean;
    readonly onCancelClick: () => void;
    readonly selectedItem: DigitalSet;
    readonly selectedTable: DigitalCorrespondanceTable;

    static $inject = [
        'storeService',
        'translatedLoggerService'
    ];

    constructor(
        private _storeService: IStoreService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.miniatures = [
            new Miniature('digitalSet', 'Digital set', 'fa-list-ol', ['Set of values used by digital tags']),
            new Miniature(correspondanceTable, 'Correspondence table', 'fa-tree', ['Links multiple digital sets together'])
        ];
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedItem'] && this.selectedItem) {
            this.initDigitalSetProperties();
            this.editedItem = this.selectedItem;
        } else if (changesObj['selectedTable'] && this.selectedTable) {
            this.initCorrespondanceTableProperties();
            this.editedItem = this.selectedTable;
        } else if (!this.selectedItem && !this.selectedTable) {
            this.editedItem = null;
        }
    }

    onSave(): angular.IPromise<angular.IAngularEvent | void> {
        let promise: angular.IPromise<any>;
        let verb: string;
        let objectType: string;
        let object: INamedGuid;
        if (this.selectedTable) {
            verb = this.selectedTable.guid === guidEmpty ? 'create' : 'update';
            objectType = 'Correspondance table';
            object = this.selectedTable;
            const service = this._storeService.dispatchers().digitalCorrespondanceTables;
            promise = this.selectedTable.guid === guidEmpty
                ? service.addOne(this.selectedTable)
                : service.updateOne(this.selectedTable);
        } else if (this.selectedItem) {
            const digitalSet = this.selectedItem;
            verb = digitalSet.guid === guidEmpty ? 'create' : 'update';
            objectType = 'Set';
            object = digitalSet;
            promise = this._storeService.dispatchers().digitalSets
                .updateOne(digitalSet);
        }
        return promise
            .then(data => {
                this.formController?.$setPristine();
                this._loggerService.logSuccess(`${objectType} ${object.name} successfully ${verb}d`);
            })
            .catch(() => {
                this._loggerService.logError(`Failed to ${verb} ${objectType} ${object.name}`);
            });
    }

    onCancel() {
        this.formController?.$setPristine();
        if (this.onCancelClick) {
            this.onCancelClick();
        }
    }

    private initDigitalSetProperties() {
        this.properties = this.getFields(this.allSets, this.selectedItem);
    }

    private initCorrespondanceTableProperties() {
        this.properties = this.getFields(this.allCorrespondanceTables, this.selectedTable);
    }

    private getFields(existingItems: INamedGuid[], editedItem: INamedGuid): ContextualField[] {
        const nameField = angular.copy(Fields.Name);
        nameField.placeholder = 'Name';
        nameField.isValid = (newName: string) => {
            const isUnique = existingItems
                .filter(s => s.name === newName && s.guid !== editedItem.guid).length === 0;
            return {
                'duplicate': isUnique
            };
        };
        nameField.messages = (nameField.messages || [])
            .concat([{
                key: 'levels',
                value: 'At least two levels are required'
            }]);
        const documentationField = angular.copy(Fields.Documentation);
        documentationField.extraData = {
            placement: 'left-top'
        };
        const properties: ContextualField[] = [
            nameField,
            angular.copy(Fields.ShortComment),
            angular.copy(Fields.LongComment),
            documentationField
        ];
        return properties;
    }
}

export const wtDigitalSetContextual: ComponentDefinition = {
    componentId: 'wtDigitalSetContextual',
    controller: DigitalSetContextualController,
    template: require('./digitalSetContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        allSets: '<',
        allCorrespondanceTables: '<?',
        formController: '=?',
        isDirtyFromOutside: '<?',
        isInvalidFromOutside: '<?',
        onCancelClick: '&?',
        selectedItem: '<?',
        selectedTable: '<?'
    }
};